import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Resource } from '../../../interfaces/recursos/resource.entity';
import { ResourceCategory } from '../../../interfaces/recursos/resource-category.entity';
import { ResourceDelivery } from '../../../interfaces/recursos/resource-delivery.entity';
import { ResourceInventory } from '../../../interfaces/recursos/resource-inventory.entity';
import { Reservation } from '../../../interfaces/recursos/reserve.entity';
import { Project } from '../../../interfaces/recursos/projects.entity';
import { DeliveryReservation } from '../../../interfaces/recursos/delivery-reservation.entity';
import { clientHeaders } from '../../../utils/services';

export type ResourceToSend = {
	id_resource: string;
	serial: string;
	description: string;
	clase?: string;
	color?: string;
	marca?: string;
	modelo?: string;
	serie?: string;
	estado?: string;
	valor?: number;
	activo?: boolean;
	ubicacion?: string;
}

export type InventaryInstitution = {
	count: number;
	status: string;
	resource_id: string;
	resource: string;
	category_id: string;
	category: string;
	delivery_id: string;
	project_id?: string;
	project?: string;
}

export type ResourceStatus = {
	id_inventary: string;
	status: string;
}

export type ResourceReservation = {
	id_resource: string;
	quantity: number;
	id_project: string;
}

export type ItemSerial = {
	id: string;
	serial: string;
	apply_serial: boolean;
	serialIn: string[];
};

export type ResourceSerialItem = {
	id: string;
	avaliable: ItemSerial[];
}

export type DeliveryReservationResource = {
	id?: string;
	id_inventary?: string;
	observation?: string;
	received?: boolean;
}

export type NewResourceType = {
	id?: string;
	name: string;
	description?: string;
	category: string;
}

export type ResourceWithCount = Resource & {
	count: number;
}

export type ProjectWithResources = Project & {
	id_delivery: string,
	delivery_description: string,
	resources: ResourceWithCount[]
}

export type ResourceWithValidations = Omit<Resource,'inventaries' | 'resourceReservations' | 'projectResources'> & {
	inventaries: boolean;
	resourceReservations: boolean;
	projectResources: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class RecursosService {
	private baseUrl: string = environment.baseUrl;
	private reportsUrl = `${environment.baseUrl}/reports`;

	constructor(private http: HttpClient) { }

	getById(resourceId?: string): Observable<NewResourceType | null > {
		if (resourceId) {
			return this.http.get<NewResourceType>(`${this.baseUrl}/resources/resource/${resourceId}`).pipe(
				map((resource) => this.mapResource(resource))
			);
		}
		return of(null);
	}

	getResources(projectId?: string) {
		let params = new HttpParams();
		if (projectId) {
			params = params.append('id_project', projectId);
		}
		return this.http.get<ResourceWithValidations[]>(`${this.baseUrl}/resources/resources`, {
			params
		});
	}

	getCategories() {
		return this.http.get<ResourceCategory[]>(`${this.baseUrl}/resources/categories`);
	}

	createDelivery(
		delivery_date: string,
		description: string,
		id_institution: string,
		resources: ResourceToSend[],
		project?: string,
	) {
		return this.http.post<{ id: string }>(`${this.baseUrl}/resources/delivery`, {
			delivery_date,
			description,
			id_institution,
			project,
			resources
		});
	}

	uploadEvidenceDelivery(
		deliveryId: string,
		evidence?: File | null,
	) {
		const formData = new FormData();
		if (evidence) {
			formData.append('evidence', evidence);
		}

		return this.http.post<{
			id: string | null,
			filename: string
		}>(`${this.baseUrl}/resources/delivery/file/${deliveryId}`, formData, {
			headers: clientHeaders,
			withCredentials: true
		});
	}

	getDeliveries(institutionId: string) {
		return this.http.get<ResourceDelivery[]>(`${this.baseUrl}/resources/deliveries/${institutionId}`);
	}

	getInventaryInstitution(institutionId: string) {
		return this.http.get<InventaryInstitution[]>(`${this.baseUrl}/resources/resources-institution/${institutionId}`);
	}

	getInventaryByDelivery(deliveryId?: string, resourceId?: string, ownResources?: boolean) {
		let params = new HttpParams();
		if (deliveryId) {
			params = params.append('id_delivery', deliveryId);
		}
		if (resourceId) {
			params = params.append('id_resource', resourceId);
		}
		if (ownResources) {
			params = params.append('own_resources', ownResources);
		}

		return this.http.get<ResourceInventory[]>(`${this.baseUrl}/resources/resources-inventary`, {
			params
		});
	}

	updateInventaryStatuses(resources: ResourceStatus[]) {
		return this.http.patch(`${this.baseUrl}/resources/inventary`, {
			resources
		}, { responseType: 'text' });
	}

	createReservation(institutionId: string, reservation: Reservation, resources: ResourceReservation) {
		return this.http.post<{ id: string }>(`${this.baseUrl}/resources/reservation/${institutionId}`, {
			...reservation,
			resources
		});
	}

	getReservations(institutionId: string, isAdmin = false) {
		const path = isAdmin ? 'reservation-institution-admin' : 'reservation-institution';
		return this.http.get<Reservation[]>(`${this.baseUrl}/resources/${path}/${institutionId}`);
	}

	deleteReservation(reservationId: string) {
		return this.http.delete(`${this.baseUrl}/resources/reservation/${reservationId}`, {
			responseType: 'text'
		});
	}

	getProjectInstitution() {
		return this.http.get<Project[]>(`${this.baseUrl}/resources/projects-institution`);
	}

	getProjectsWithResources(start_date: string, end_date: string, id_institution: string) {
		const params = new HttpParams({
			fromObject: {
				start_date,
				end_date,
				id_institution
			}
		});

		return this.http.get<ProjectWithResources[]>(`${this.baseUrl}/resources/projects-with-resources`, {
			params
		});
	}

	getReservation(reservationId: string) {
		return this.http.get<{
			reservation: Reservation,
			resources: ResourceSerialItem[]
		}>(`${this.baseUrl}/resources/reservation/${reservationId}`, {
			params: {
				detail: true
			}
		});
	}

	createDeliveryReservation(reservationId: string, institutionId: string, resources: DeliveryReservationResource[]) {
		return this.http.post<{ id: string }>(`${this.baseUrl}/resources/delivery-reservation/${reservationId}`, {
			id_institution: institutionId,
			resources
		});
	}

	getDeliveryReservation(reservationId: string) {
		return this.http.get<DeliveryReservation>(`${this.baseUrl}/resources/delivery-reservation/${reservationId}`);
	}

	updateDeliveryReservation(deliveryReservationId: string, resources: DeliveryReservationResource[]) {
		return this.http.patch(`${this.baseUrl}/resources/delivery-reservation/${deliveryReservationId}`, {
			resources
		}, { responseType: 'text' });
	}

	createResource(resource: NewResourceType) {
		return this.http.post<Resource>(`${this.baseUrl}/resources/resource`, resource);
	}

	updateResource(resourceId: string, resource: NewResourceType) {
		return this.http.put<Resource>(`${this.baseUrl}/resources/resource/${resourceId}`, resource);
	}

	validateSerial(serial: string, institutionId: string) {
		return this.http.post<boolean>(`${this.baseUrl}/resources/validate-serial`, { serial, institutionId });
	}

	getDelivery(deliveryId: string){
		return this.http.get<ResourceDelivery>(`${this.baseUrl}/resources/delivery/${deliveryId}`);
	}

	downloadXlsx(pathName: string) {
		return this.http.get(`${this.reportsUrl}/${pathName}`, {
			responseType: 'arraybuffer'
		});
	}

	private mapResource(resource: NewResourceType): NewResourceType {
		return {
			id: resource.id,
			name: resource.name,
			description: resource.description,
			category: resource.category
		};
	}

	downloadFile(resource: string, filename: string): Observable<any>{
		return this.http.get(`${this.baseUrl}/resources/downloadFile/${resource}/${filename}`, { responseType: 'blob' });
	}
}